import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-date-picker';
import Select from 'react-select';
import moment from 'moment';
import {
  GetLocationRoutes,
  GetOpsLocationsStockingDash,
  ListDrivers,
  BulkUpdateLocationRoutes,
} from '../../libs/GraphQL';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import { formatAccountTier } from '../../utils/accountTier';
import { isSampleDeliveryOpportunity } from '../../utils/sampleDelivery';
import Loader from '../../components/atoms/Loader';
import StatusPill from '../../components/atoms/StatusPill';
import Badge from '../../components/atoms/Badge';
import SegmentedToggle from '../../components/atoms/SegmentedToggle';
import AlertBox from '../../components/atoms/AlertBox';
import RouteHeaderRow from './RouteHeaderRow';
import SaveAssignmentsBar from './SaveAssignmentsBar';

const StyledDiv = styledComponent('div', {
  '& .mainHeading': {
    color: '#000000',
    fontSize: '28px',
    fontWeight: 'normal',
    letterSpacing: '0.21px',
    marginTop: '24px',
  },
  '& .subHeading': {
    marginTop: '16px',
    fontSize: '18px',
    letterSpacing: '0.14px',
    color: '#000000',
    marginRight: '72px',
  },
  '& .wrap': {
    display: 'flex',
    alignItems: 'center',
  },
  '#DatePicker': {
    marginTop: '18px',
    marginRight: '15px',
    position: 'relative',
    zIndex: '100',
  },
  '#MarketPicker': {
    marginTop: '18px',
    marginRight: '15px',
    position: 'relative',
    zIndex: '100',
  },
  '#RouteTable': {
    marginTop: '48px',
    width: '100%',
    borderSpacing: '50px 12px',
    border: '1px solid black',
    '& thead': {
      th: {
        padding: '8px',
      },
    },
    'td, th': {
      padding: '4px 12px',
      border: '1px solid black',
      color: '#000000',
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },
    th: {
      fontSize: '18px',
      textAlign: 'center',
      position: 'sticky',
      top: '0',
      zIndex: '1',
      background: '#ffffff',
    },
    td: {
      fontSize: '16px',
      opacity: '1',
      letterSpacing: '0.63px',
      height: '40px',

      '&.alignCenter': {
        textAlign: 'center',
      },
    },
    '& .header': {
      'td, th': {
        padding: '8px',
        fontSize: '20px',
        fontWeight: 'bold',
      },
    },
    '& .summary': {
      borderBottom: '2px solid black',
    },
    '& .locationCell': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.gapMd,
    },
    '& .locationCellPills': {
      marginLeft: 'auto',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.gapMd,
    },
  },
  '#EmptyRoutes': {
    marginTop: '24px',
    fontSize: '18px',
    backgroundColor: '#eeeeee',
    textAlign: 'center',
  },
  '#Loading': {
    padding: '32px',
    backgroundColor: '#ffffff',
  },
  '& .modeToggle': {
    marginTop: theme.spacing.toolbarGap,
  },
});

function getRouteColor(route) {
  const MAX_HUE_NUMBER = 360;
  const routeAsHashcode = route
    .split('')
    .reduce((hashCode, letter) => hashCode + letter.charCodeAt(0), 0);
  // This random number is used to make the color difference more distinct,
  // for silimar route names (e.g route_name1, route_name2, route_name3)
  const DISTINCTION_NUMBER = 22.5;

  return `hsl(${
    (routeAsHashcode * DISTINCTION_NUMBER) % MAX_HUE_NUMBER
  }, 100%, 85%)`;
}

export default class ViewWeeklyRoutes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      date: new Date(),
      locationRoutes: {
        routes: [],
      },
      sortedData: [],
      keptData: [],
      displayData: [],
      markets: [
        { value: 'ALL', label: 'ALL' },
        { value: 'MW', label: 'MW' },
        { value: 'NE', label: 'NE' },
        { value: 'WC', label: 'WC' },
        { value: 'S', label: 'S' },
      ],
      selectedMarket: { value: 'ALL', label: 'ALL' },
      isLoading: false,
      accountTierById: {},
      badgeNameById: {},
      isSampleDeliveryById: {},
      assignMode: false,
      drivers: [],
      driversError: false,
      driversLoading: false,
      draftAssignments: {},
      draftNames: {},
      collapsedRoutes: [],
      isSaving: false,
      saveError: null,
      saveSuccess: false,
    };
    this.driversRequestId = 0;
    this.getLocationRoutes = this.getLocationRoutes.bind(this);
    this.getLocationMeta = this.getLocationMeta.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.appendZeroToFormat = this.appendZeroToFormat.bind(this);
    this.formatDateObject = this.formatDateObject.bind(this);
    this.onModeChange = this.onModeChange.bind(this);
    this.fetchDrivers = this.fetchDrivers.bind(this);
    this.onDriverChange = this.onDriverChange.bind(this);
    this.onNameSave = this.onNameSave.bind(this);
    this.onToggleCollapse = this.onToggleCollapse.bind(this);
    this.onSaveAssignments = this.onSaveAssignments.bind(this);
  }

  async componentDidMount() {
    try {
      this.getLocationRoutes(this.state.date);
      this.getLocationMeta();
    } catch (e) {
      console.error(e);
    }
  }

  async getLocationMeta() {
    /*
      LocationRoute doesn't expose accountTier, badgeName or the Salesforce
      opportunity, so pull them from OpsLocations (LIVE) and build
      id -> accountTier, id -> badgeName and id -> isSampleDelivery maps.
      Reuses the per-day 'opsLocations' localStorage cache the Stocking
      Dashboard populates.

      OpsLocation has no badgeName field yet (not requested — see GraphQL.js),
      so badgeNameById stays empty until that field ships. It's still built
      tolerantly from item.badgeName so it lights up automatically once the
      backend adds it.
    */
    try {
      const today = moment().format('YYYY-MM-DD');
      const cached = localStorage.getItem('opsLocations');
      let allLocations = [];

      const parsedCache = cached ? JSON.parse(cached) : null;
      const cacheUsable = parsedCache && parsedCache.date === today;

      if (cacheUsable) {
        allLocations = parsedCache.locations || [];
      } else {
        let nextToken = null;

        do {
          const response = await GetOpsLocationsStockingDash(nextToken, [
            'LIVE',
          ]);
          if (response?.data?.opsLocations) {
            const locations = response.data.opsLocations.locations || [];
            allLocations.push(...locations);
          }
          nextToken = response?.data?.opsLocations?.nextToken;
        } while (nextToken);

        try {
          localStorage.setItem(
            'opsLocations',
            JSON.stringify({
              date: today,
              locations: allLocations,
            }),
          );
        } catch (cacheError) {
          // Quota exceeded or storage unavailable — the fetched locations
          // are still used for this page load; only the per-day cache is skipped.
          console.warn('Could not cache ops locations:', cacheError);
        }
      }

      const accountTierById = {};
      const badgeNameById = {};
      const isSampleDeliveryById = {};
      allLocations.forEach((item) => {
        accountTierById[item.id] = item.accountTier || '';
        if (item.badgeName) {
          badgeNameById[item.id] = item.badgeName;
        }
        if (isSampleDeliveryOpportunity(item.opportunity)) {
          isSampleDeliveryById[item.id] = true;
        }
      });
      this.setState({ accountTierById, badgeNameById, isSampleDeliveryById });
    } catch (error) {
      console.error('Error fetching ops locations:', error);
    }
  }

  appendZeroToFormat(val) {
    /*
            Takes date / month as input and appends 0 to it if value is < 10
            Example: if date is 9, this method returns '09'

            @param val: int
            return: String
        */
    if (val < 10) return `0${val.toString()}`;
    return val.toString();
  }

  formatDateObject(date) {
    /*
            Takes Date object as input and formats it to "YYYY-mm-dd" string

            @param date: Date.prototype
            return: String
        */
    let dateString = '';
    dateString += `${date.getFullYear().toString()}-${this.appendZeroToFormat(
      date.getMonth() + 1,
    )}-${this.appendZeroToFormat(date.getDate())}`;
    return dateString;
  }

  async getLocationRoutes(date) {
    this.setState({ isLoading: true });
    try {
      await GetLocationRoutes({
        stockingDate: this.formatDateObject(date),
        calculateTotes: true,
        includeOnlineDate: true,
      }).then((data) => {
        this.setState({ locationRoutes: data });
        // there's a data duplication: raw and sorted, decided to keep both in state
        const sortedData = this.sortRoutesData(data.routes);
        this.setState({ sortedData });
        // keptData is initial sorted data after query before filtering
        this.setState({ keptData: sortedData });
        this.filterByMarket(this.state.selectedMarket);
      });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  // Route-level persisted values live redundantly on every stop row; read
  // them off the first row of the group.
  persistedByRoute() {
    const map = {};
    (this.state.locationRoutes.routes || []).forEach((row) => {
      if (!map[row.route]) {
        map[row.route] = {
          driverEmail: row.driverEmail || null,
        };
      }
    });
    return map;
  }

  // Route -> market, read off the first row of the group. Used to batch
  // saves per market (the backend contract takes a single market per call).
  marketByRoute() {
    const map = {};
    (this.state.locationRoutes.routes || []).forEach((row) => {
      if (!map[row.route]) {
        map[row.route] = row.market;
      }
    });
    return map;
  }

  effectiveDriver(routeKey, persisted) {
    return routeKey in this.state.draftAssignments
      ? this.state.draftAssignments[routeKey]
      : persisted[routeKey]?.driverEmail || null;
  }

  computeDirtyUpdates() {
    // Returns BulkUpdateLocationRoutes `updates` entries for changed routes
    // only, coalesced into one object per route.
    // driverEmail: absent = unchanged; explicit null = clear.
    // newRoute: present only when the draft name differs from the route key.
    const persisted = this.persistedByRoute();
    const dirty = {};
    Object.keys(this.state.draftAssignments).forEach((routeKey) => {
      const draft = this.state.draftAssignments[routeKey];
      if (draft !== (persisted[routeKey]?.driverEmail || null)) {
        dirty[routeKey] = {
          ...(dirty[routeKey] || { currentRoute: routeKey }),
          driverEmail: draft,
        };
      }
    });
    Object.keys(this.state.draftNames).forEach((routeKey) => {
      const draft = this.state.draftNames[routeKey];
      if (draft !== routeKey) {
        dirty[routeKey] = {
          ...(dirty[routeKey] || { currentRoute: routeKey }),
          newRoute: draft,
        };
      }
    });
    return Object.values(dirty);
  }

  confirmDiscardDrafts() {
    if (this.computeDirtyUpdates().length === 0) return true;
    return window.confirm('Discard unsaved assignment changes?');
  }

  onModeChange(mode) {
    const currentMode = this.state.assignMode ? 'assign' : 'view';
    if (mode === currentMode) return;
    if (mode === 'view' && !this.confirmDiscardDrafts()) return;
    this.setState({
      assignMode: mode === 'assign',
      draftAssignments: {},
      draftNames: {},
      saveError: null,
      saveSuccess: false,
    });
    if (mode === 'assign') {
      this.fetchDrivers(this.state.selectedMarket);
    }
  }

  async fetchDrivers(market) {
    this.driversRequestId = (this.driversRequestId || 0) + 1;
    const requestId = this.driversRequestId;
    this.setState({ driversError: false, driversLoading: true });
    try {
      // 'ALL' has no backend meaning — omit the arg (returns allRegions drivers).
      const marketArg = market.value === 'ALL' ? null : market.value;
      const drivers = await ListDrivers(marketArg);
      const sorted = [...(drivers || [])].sort((a, b) =>
        a.emailAddress.localeCompare(b.emailAddress),
      );
      if (requestId === this.driversRequestId) {
        this.setState({ drivers: sorted, driversLoading: false });
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      if (requestId === this.driversRequestId) {
        this.setState({
          drivers: [],
          driversError: true,
          driversLoading: false,
        });
      }
    }
  }

  onDriverChange(routeKey, emailOrNull) {
    this.setState((prev) => ({
      draftAssignments: { ...prev.draftAssignments, [routeKey]: emailOrNull },
      saveSuccess: false,
      saveError: null,
    }));
  }

  onNameSave(routeKey, trimmedName) {
    // draftNames[routeKey] means "pending new route key"; a draft rename is
    // dirty when it differs from the route key itself.
    const current = this.state.draftNames[routeKey] ?? routeKey;
    if (trimmedName === current) return;
    this.setState((prev) => ({
      draftNames: { ...prev.draftNames, [routeKey]: trimmedName },
      saveSuccess: false,
      saveError: null,
    }));
  }

  onToggleCollapse(routeKey) {
    this.setState((prev) => ({
      collapsedRoutes: prev.collapsedRoutes.includes(routeKey)
        ? prev.collapsedRoutes.filter((k) => k !== routeKey)
        : [...prev.collapsedRoutes, routeKey],
    }));
  }

  async onSaveAssignments() {
    const updates = this.computeDirtyUpdates();
    if (updates.length === 0) return;

    const marketByRoute = this.marketByRoute();
    const updatesByMarket = {};
    updates.forEach((update) => {
      const market = marketByRoute[update.currentRoute];
      updatesByMarket[market] = [...(updatesByMarket[market] || []), update];
    });
    const markets = Object.keys(updatesByMarket);
    const stockingDate = this.formatDateObject(this.state.date);

    this.setState({ isSaving: true, saveError: null, saveSuccess: false });

    const results = await Promise.allSettled(
      markets.map((market) =>
        BulkUpdateLocationRoutes({
          stockingDate,
          market,
          updates: updatesByMarket[market],
        }),
      ),
    );

    const failedMarkets = [];
    let firstErrorMessage = null;
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        failedMarkets.push(markets[index]);
        if (firstErrorMessage === null) {
          firstErrorMessage = result.reason && result.reason.message;
        }
        console.error('Error saving route assignments:', result.reason);
      }
    });

    if (failedMarkets.length === 0) {
      this.setState({
        draftAssignments: {},
        draftNames: {},
        saveSuccess: true,
        saveError: null,
      });
    } else {
      // Keep only drafts for routes whose market failed — succeeded markets'
      // route keys may have changed (renames), so their drafts are dropped.
      const failedRouteKeys = new Set(
        updates
          .filter((update) =>
            failedMarkets.includes(marketByRoute[update.currentRoute]),
          )
          .map((update) => update.currentRoute),
      );
      const keepDrafts = (drafts) =>
        Object.keys(drafts).reduce((acc, routeKey) => {
          if (failedRouteKeys.has(routeKey)) {
            acc[routeKey] = drafts[routeKey];
          }
          return acc;
        }, {});
      this.setState((prev) => ({
        draftAssignments: keepDrafts(prev.draftAssignments),
        draftNames: keepDrafts(prev.draftNames),
        saveSuccess: false,
        saveError: `Failed to save ${failedMarkets.join(', ')} market${
          failedMarkets.length > 1 ? 's' : ''
        }.${
          firstErrorMessage ? ` ${firstErrorMessage}` : ''
        } Your changes for the failed market are still here — try again.`,
      }));
    }

    // Renamed routes change their grouping key and color, so an in-place
    // merge is no longer possible — refetch to resync (including succeeded
    // markets on a partial failure).
    await this.getLocationRoutes(this.state.date);
    this.setState({ isSaving: false });
  }

  onDateChange(date) {
    if (!this.confirmDiscardDrafts()) return;
    this.setState({
      date,
      draftAssignments: {},
      draftNames: {},
      collapsedRoutes: [],
      saveError: null,
      saveSuccess: false,
    });
    this.getLocationRoutes(date);
  }

  filterByMarket(market) {
    if (market.value === 'ALL') {
      // fallback to initial data when market changed to ALL
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ displayData: this.state.keptData });
    } else {
      const removedNulls = this.state.sortedData.filter(function (el) {
        return el.market != null;
      });
      const filtered = removedNulls.filter(
        (route) => route.market.toUpperCase() === market.value,
      );
      // displayData is container to draw the table, sorted and filtered
      this.setState({ displayData: filtered });
    }
  }

  onMarketChange(market) {
    /*
          Takes market say "NE" from the dropdown and filters routs ids
          Caches selected market and sorted data on the first page load
          Updates sortedData to display the table
         */
    if (!this.confirmDiscardDrafts()) return;
    this.setState({
      selectedMarket: market,
      draftAssignments: {},
      draftNames: {},
      collapsedRoutes: [],
      saveError: null,
      saveSuccess: false,
    });
    this.filterByMarket(market);
    if (this.state.assignMode) {
      this.fetchDrivers(market);
    }
  }

  sortRoutesData(items) {
    /*
        Takes items (list of dictionaries with routes data) as input
        Compares items to sort them corresponding to the route
        and if the names are of the same route compares stop number
        Example: for routes, a = 5 b = 1 if return b, a
                if rout names are equal places items asc by stop
        return: sorted routes data
    */
    const sortableItems = [...items];
    sortableItems.sort((a, b) => {
      if (a.route < b.route) {
        return -1;
      }
      if (a.route > b.route) {
        return 1;
      }
      return a.stop - b.stop;
    });
    return sortableItems;
  }

  render() {
    const { canAssign } = this.props;
    const persisted = this.persistedByRoute();
    const groupedRoutes = {};
    this.state.displayData.forEach((route) => {
      if (!groupedRoutes[route.route]) {
        groupedRoutes[route.route] = [];
      }
      groupedRoutes[route.route].push(route);
    });
    const routeKeys = Object.keys(groupedRoutes);
    const assignedCount = routeKeys.filter((key) =>
      this.effectiveDriver(key, persisted),
    ).length;
    const dirtyCount = this.computeDirtyUpdates().length;

    return (
      <StyledDiv>
        <div>
          <div className="mainHeading">View Routes By Date</div>
        </div>
        <div className="wrap">
          <div className="subHeading">Select Date</div>
          <div className="subHeading">Select Region</div>
        </div>
        <div className="wrap">
          <div id="DatePicker">
            <DatePicker
              onChange={(date) => this.onDateChange(date)}
              value={this.state.date}
              format="MM/dd/y"
            />
          </div>
          <div id="MarketPicker">
            <Select
              options={this.state.markets}
              onChange={(market) => this.onMarketChange(market)}
              value={this.state.selectedMarket}
            />
          </div>
        </div>
        {canAssign && (
          <div className="modeToggle">
            <SegmentedToggle
              ariaLabel="Page mode"
              options={[
                { value: 'view', label: 'View' },
                { value: 'assign', label: 'Assign Drivers' },
              ]}
              value={this.state.assignMode ? 'assign' : 'view'}
              onChange={this.onModeChange}
            />
          </div>
        )}
        {this.state.assignMode && (
          <>
            <SaveAssignmentsBar
              assignedCount={assignedCount}
              totalCount={routeKeys.length}
              isDirty={dirtyCount > 0}
              isSaving={this.state.isSaving}
              saveSuccess={this.state.saveSuccess}
              onSave={this.onSaveAssignments}
            />
            {this.state.saveError && (
              <AlertBox title="Save failed">{this.state.saveError}</AlertBox>
            )}
          </>
        )}
        <div>
          {this.state.isLoading ? (
            <div id="Loading">
              <Loader />
            </div>
          ) : this.state.locationRoutes.routes &&
            this.state.locationRoutes.routes.length > 0 ? (
            <table id="RouteTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Region</th>
                  <th>Stop</th>
                  <th>Location</th>
                  <th>Account Tier</th>
                  <th>Badge Name</th>
                  <th>Tote Count</th>
                  <th>Stop Type</th>
                  <th>Nextmv Run ID</th>
                </tr>
              </thead>
              <tbody>
                {routeKeys.map((routeKey) => {
                  const routeData = groupedRoutes[routeKey];
                  const totalStops = routeData.length;
                  const isCollapsed =
                    this.state.collapsedRoutes.includes(routeKey);

                  return (
                    <React.Fragment key={`route-${routeKey}`}>
                      <RouteHeaderRow
                        key={`header-${routeKey}`}
                        routeKey={routeKey}
                        displayName={this.state.draftNames[routeKey] ?? null}
                        routeColor={getRouteColor(routeKey)}
                        isAssignMode={this.state.assignMode}
                        isCollapsed={isCollapsed}
                        onToggleCollapse={() => this.onToggleCollapse(routeKey)}
                        drivers={this.state.drivers}
                        driversError={this.state.driversError}
                        driversLoading={this.state.driversLoading}
                        selectedDriverEmail={this.effectiveDriver(
                          routeKey,
                          persisted,
                        )}
                        onDriverChange={(email) =>
                          this.onDriverChange(routeKey, email)
                        }
                        onNameSave={(name) => this.onNameSave(routeKey, name)}
                      />
                      {!isCollapsed && (
                        <>
                          {routeData.map((route) => {
                            const twelveWeeksAfterOnlineDate = moment(
                              route.onlineDate,
                            )
                              .add(12, 'w')
                              .startOf('day');
                            const showNewFridge =
                              route.onlineDate &&
                              twelveWeeksAfterOnlineDate.isAfter(
                                moment().startOf('day'),
                              );
                            const showSampleDelivery = Boolean(
                              this.state.isSampleDeliveryById[route.id],
                            );
                            return (
                              <tr
                                key={`${route.route}-${route.stop}`}
                                style={{
                                  background: getRouteColor(route.route),
                                }}
                              >
                                <td>{route.stockingDate}</td>
                                <td className="alignCenter">{route.market}</td>
                                <td className="alignCenter">{route.stop}</td>
                                <td>
                                  <div className="locationCell">
                                    <span>{route.id}</span>
                                    <div className="locationCellPills">
                                      {showNewFridge && (
                                        <StatusPill
                                          text="Priority: New Fridge"
                                          width="200px"
                                          height="30px"
                                        />
                                      )}
                                      {showSampleDelivery && (
                                        <StatusPill
                                          text="Priority: Sample Delivery"
                                          width="240px"
                                          height="30px"
                                          style={{ maxWidth: '240px' }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="alignCenter">
                                  {formatAccountTier(
                                    this.state.accountTierById[route.id],
                                  )}
                                </td>
                                <td className="alignCenter">
                                  {this.state.badgeNameById[route.id] ? (
                                    <Badge variant="access">
                                      {this.state.badgeNameById[route.id]}
                                    </Badge>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="alignCenter">
                                  {route.toteCountPerStop}
                                </td>
                                <td
                                  style={{
                                    fontWeight:
                                      route.stopType === 'REMOVAL'
                                        ? 'bold'
                                        : '',
                                  }}
                                >
                                  {route.stopType}
                                </td>
                                <td className="alignCenter">
                                  {route.nextmvRunId || '—'}
                                </td>
                              </tr>
                            );
                          })}
                          <tr
                            key={`summary-${routeKey}`}
                            className="summary"
                            style={{
                              background: getRouteColor(routeKey),
                              fontWeight: 'bold',
                            }}
                          >
                            <td colSpan="2">SUMMARY</td>
                            <td className="alignCenter">{totalStops}</td>
                            <td aria-hidden="true" />
                            <td aria-hidden="true" />
                            <td aria-hidden="true" />
                            <td className="alignCenter">
                              {routeData[0]?.toteCountPerRoute || ''}
                            </td>
                            <td aria-hidden="true" />
                            <td aria-hidden="true" />
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div id="EmptyRoutes">No routes available for given date</div>
          )}
        </div>
      </StyledDiv>
    );
  }
}

ViewWeeklyRoutes.propTypes = {
  canAssign: PropTypes.bool,
};

ViewWeeklyRoutes.defaultProps = {
  canAssign: false,
};
